import React, { Fragment, useState, useEffect } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { InfoCircle } from "iconsax-react";
import { useForm } from "react-hook-form";
import beveatApiResourceOwner from "../../api/resource_owner_beveat_api.js";
import { commonConfigs, urls } from "../../../config.js";
import { getUser } from "../../services/local_service.js";
import {
  CERTIFICATE_PROFICIENCY_SYSCODE,
  CertificateProficiencySyscodeString
} from "../../constants/certificate_proficiency_syscode.jsx";

export default function UpdateCertificateDetailsModal({
  isOpen,
  setOpenModal,
  role = "developer", // Default to developer logic
  data,
  planId,
  devProjId,
  activeTab,
  onUpdateSuccess
}) {
  const [isUpdating, setIsUpdating] = useState(false);
  const { register, handleSubmit, formState: { errors }, reset } = useForm({
    defaultValues: {
      developer_name: data?.developer_name || data?.name || "",
      developer_email: data?.developer_email || data?.email || "",
      proficiency: data?.proficiency || ""
    }
  });

  // Sync form state when external data prop changes
  useEffect(() => {
    reset({
      developer_name: data?.developer_name || data?.name || "",
      developer_email: data?.developer_email || data?.email || "",
      proficiency: data?.proficiency || ""
    });
  }, [data, reset]);

  const onSubmit = async (formData) => {
    setIsUpdating(true);
    const updateUrl = urls.resourceOwnerUrl + commonConfigs.apiUrls.updateCertificate();

    try {
      // 1. Primary Update (Name and Email)
      const basePayload = {
        org_invoice_plan_id: planId || data?.orgInvoicePlanId,
        developer_proj_id: devProjId || data?.developerProjId,
        developer_name: formData.developer_name,
        developer_email: formData.developer_email,
        account_id: getUser()?.account_id
      };

      await beveatApiResourceOwner.put(updateUrl, basePayload, {
        headers: commonConfigs.resourceOwnerApiHeader,
      });

      // 2. Admin-specific logic: Update Proficiency if in Certified tab
      if (role === "admin" && activeTab === "Certified" && formData.proficiency) {
        const proficiencyPayload = {
          developer_email: formData.developer_email,
          developer_certificate_id: data?.developerCertificateId,
          certificate_proficiency_syscode: parseInt(formData.proficiency)
        };
        await beveatApiResourceOwner.put(updateUrl, proficiencyPayload, {
          headers: commonConfigs.resourceOwnerApiHeader,
        });
      }

      alert("Certificate updated successfully!");
      setOpenModal(false);

      if (onUpdateSuccess) {
        onUpdateSuccess();
      } else {
        window.location.reload();
      }

    } catch (err) {
      console.error("Update Error:", err);
      alert("Failed to update. Please check the console for details.");
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="fixed z-50 inset-0 overflow-y-auto" onClose={() => setOpenModal(false)}>
        <div className="flex items-center justify-center min-h-screen px-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100"
            leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-slate-900/50" />
          </Transition.Child>

          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100"
            leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95"
          >
            <div className="inline-block bg-white rounded-lg p-6 shadow-2xl transform transition-all sm:max-w-lg sm:w-full">
              <form onSubmit={handleSubmit(onSubmit)}>
                <Dialog.Title as="h3" className="text-xl font-bold text-gray-900 mb-6">
                  {role === 'admin' ? 'Edit Certificate (Admin)' : 'Update Certificate Details'}
                </Dialog.Title>

                <div className="space-y-5">
                  {/* Name Input */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Name on Certificate</label>
                    <div className="relative">
                      <input
                        {...register("developer_name", { required: true })}
                        placeholder="Enter full name"
                        className={`w-full p-2.5 border rounded-md focus:ring-2 focus:ring-blue-500 outline-none ${errors.developer_name ? 'border-red-500' : 'border-gray-300'
                          }`}
                      />
                      {errors.developer_name && (
                        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                          <InfoCircle className="h-5 w-5 text-red-500" />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Email Input */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">School/IHLs/Adult Learning Institute Email</label>
                    <div className="relative">
                      <input
                        {...register("developer_email", { required: true })}
                        placeholder="Enter email"
                        className={`w-full p-2.5 border rounded-md focus:ring-2 focus:ring-blue-500 outline-none ${errors.developer_email ? 'border-red-500' : 'border-gray-300'
                          }`}
                      />
                      {errors.developer_email && (
                        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                          <InfoCircle className="h-5 w-5 text-red-500" />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Proficiency Dropdown (Admin Only) */}
                  {role === "admin" && activeTab === "Certified" && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Proficiency Level</label>
                      <select
                        {...register("proficiency")}
                        className="w-full p-2.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 outline-none bg-white"
                      >
                        <option value="">Select Level</option>
                        {Object.entries(CERTIFICATE_PROFICIENCY_SYSCODE)
                          .filter(([key]) => key !== 'Reserved')
                          .map(([key, value]) => (
                            <option key={value} value={value}>
                              {CertificateProficiencySyscodeString[value]}
                            </option>
                          ))}
                      </select>
                    </div>
                  )}
                </div>

                <div className="mt-8 flex flex-col sm:flex-row-reverse gap-3">
                  <button
                    type="submit"
                    disabled={isUpdating}
                    className="w-full sm:w-auto px-8 py-2.5 bg-blue-600 text-white rounded-md font-bold hover:bg-blue-700 transition-colors disabled:bg-gray-400"
                  >
                    {isUpdating ? "Updating..." : "Update"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setOpenModal(false)}
                    className="w-full sm:w-auto px-8 py-2.5 border border-red-500 text-red-500 rounded-md font-bold hover:bg-red-50 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
}